import { Disclosure } from "@design";
import { Terminal } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import { parseSlashCommands, type SlashCommandProblem } from "./parseSlashCommands";

function ProblemText({ problem }: Readonly<{ problem: SlashCommandProblem }>) {
  const { t } = useTranslation("agents", { keyPrefix: "form.slashCommands.problems" });
  switch (problem.code) {
    case "missingName":
      return t("missingName", { index: problem.position });
    case "spaces":
      return t("spaces", { name: problem.name });
    case "noDescription":
      return t("noDescription", { name: problem.name });
    default:
      return t(problem.code);
  }
}

/**
 * Under the agent's instructions: the slash commands its `<slash_commands>` block declares, as people will see them
 * when they type `/` in a chat, and what's wrong with the block before it's saved. A tip when there is no block.
 */
export function SlashCommandsPreview({ systemPrompt }: Readonly<{ systemPrompt: string }>) {
  const { t } = useTranslation("agents", { keyPrefix: "form.slashCommands" });
  const parsed = useMemo(() => parseSlashCommands(systemPrompt), [systemPrompt]);

  if (!parsed.hasBlock) {
    return (
      <Text variant="caption" tone="tertiary">
        {t("tip")}
      </Text>
    );
  }
  // Nothing usable: the block itself is wrong, so it's an error; otherwise the commands work, with warnings.
  const broken = parsed.commands.length === 0;
  return (
    <div className="flex flex-col gap-1.5">
      {parsed.problems.length > 0 && (
        <ul aria-label={t("problemsLabel")} className="flex flex-col gap-0.5">
          {parsed.problems.map((problem) => (
            <li key={`${problem.code}-${problem.position}`}>
              <Text as="span" variant="caption" tone={broken ? "error" : "warning"}>
                <ProblemText problem={problem} />
              </Text>
            </li>
          ))}
        </ul>
      )}
      {!broken && (
        <Disclosure icon={<Terminal size={12} />} label={t("detected", { count: parsed.commands.length })}>
          <ul aria-label={t("previewLabel")} className="flex flex-col gap-1 p-2">
            {parsed.commands.map((command) => (
              // By position: two commands can share a name while the block is being written.
              <li key={command.position} className="flex flex-col px-3 py-1.5">
                <Text as="span" variant="code">
                  /{command.name}
                </Text>
                {command.description && (
                  <Text as="span" variant="caption" tone="tertiary">
                    {command.description}
                  </Text>
                )}
              </li>
            ))}
          </ul>
        </Disclosure>
      )}
    </div>
  );
}
